import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField,
  Grid,
  Button,
  Chip,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import { fNumber } from "../utils/formatNumber";

// services (adjust path if your services live elsewhere)
import UserService from "./services/UserService";
import SharesService from "./services/SharesService";
import SavingsService from "./services/SavingsService";
import LoanService from "./services/LoanService";
import IleyaSavingsService from "./services/IleyaSavingsService";
import DevService from "./services/DevService";
import MinService from "./services/MinService";
import WelService from "./services/WelService";
import TakafulService from "./services/TakafulService";
import SpeAccService from "./services/SpeAccService";
import BusinessService from "./services/BusinessService";
import BroughtForwardModal from "../components/BroughtForwardModal";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

const getPageRange = (current, total) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const currentNum = current + 1;
  const pages = [];
  if (currentNum <= 4) {
    for (let i = 1; i <= 5; i++) pages.push(i);
    pages.push("...");
    pages.push(total);
  } else if (currentNum >= total - 3) {
    pages.push(1);
    pages.push("...");
    for (let i = total - 4; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    pages.push("...");
    pages.push(currentNum - 1);
    pages.push(currentNum);
    pages.push(currentNum + 1);
    pages.push("...");
    pages.push(total);
  }
  return pages;
};

const servicesByKey = {
  shares: SharesService,
  savings: SavingsService,
  loan: LoanService,
  ileya: IleyaSavingsService,
  dev: DevService,
  min: MinService,
  wel: WelService,
  takaful: TakafulService,
  speacc: SpeAccService,
  business: BusinessService,
};

// pick the latest (most recent) balance entry by timestamp; fall back to array[last] when no timestamps

// Helper to escape HTML characters for safe printable documents (prevent XSS)
const escapeHtml = (str) => {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const pickLatestBalanceAndTs = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return { balance: 0, ts: null };

  const mapped = arr.map((it) => {
    const ts = it.createdAt ?? it.date ?? it.postedAt ?? it.timestamp ?? null;
    const t = ts ? new Date(ts).getTime() : NaN;
    const balance = Number(it.newBalance ?? it.balance ?? it.amount ?? 0) || 0;
    return { balance, ts, t };
  });

  const withValidTs = mapped.filter((m) => Number.isFinite(m.t));
  if (withValidTs.length > 0) {
    const latest = withValidTs.reduce((a, b) => (a.t >= b.t ? a : b));
    return { balance: latest.balance, ts: latest.ts };
  }

  // no valid timestamps — return last item in original order
  const last = mapped[mapped.length - 1];
  return { balance: last.balance, ts: last.ts };
};

const getStoredUser = () => {
  try {
    const s = localStorage.getItem("user") || localStorage.getItem("profile");
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

const normalizeRolesString = (u) => {
  if (!u) return "";
  const parts = [];
  if (Array.isArray(u.roles)) parts.push(...u.roles.map((r) => (typeof r === "object" && r !== null ? (r.name || "") : String(r))));
  else if (u.roles) parts.push(typeof u.roles === "object" && u.roles !== null ? (u.roles.name || "") : String(u.roles));
  if (u.role) parts.push(Array.isArray(u.role) ? u.role.join(" ") : (typeof u.role === "object" && u.role !== null ? (u.role.name || "") : String(u.role)));
  if (u.roleName) parts.push(String(u.roleName));
  if (u.isAdmin) parts.push("admin");
  if (u.isPresident) parts.push("president");
  return parts.join(" ").toLowerCase().replace(/role_/g, "");
};

export default function SummaryPage() {
  const navigate = useNavigate();
  const { user: reduxUser } = useSelector((state) => state.auth || {});

  // prefer redux-authenticated user, fall back to stored profile
  const effectiveUser = reduxUser || getStoredUser();
  const viewAsRole = sessionStorage.getItem("mtc_view_as_role");
  const rolesString = viewAsRole ? viewAsRole.toLowerCase().replace(/role_/g, "") : normalizeRolesString(effectiveUser);
  const isAdmin = rolesString.includes("admin") || (!viewAsRole && Boolean(effectiveUser?.isAdmin));
  const isPresident = rolesString.includes("president") || (!viewAsRole && Boolean(effectiveUser?.isPresident));
  const isFinancial = rolesString.includes("financial");
  const isFinancialII = rolesString.includes("financial_2");
  const isFinancialIII = rolesString.includes("financial_3");
  const isFinancialI =
    (isFinancial && !isFinancialII && !isFinancialIII) ||
    rolesString.includes("financial_1");
  const isBusiness = rolesString.includes("business");
  const canViewBusiness = isAdmin || isPresident || isBusiness || isFinancialI;
  const canPostBroughtForward = (isAdmin || isFinancial) && !isPresident;

  useEffect(() => {
    if (!(isAdmin || isPresident || isFinancial)) navigate("/dashboard", { replace: true });
  }, [isAdmin, isPresident, isFinancial, navigate]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bfModalOpen, setBfModalOpen] = useState(false);
  const [selectedBfMember, setSelectedBfMember] = useState(null);
  const [allMembers, setAllMembers] = useState([]);

  // Fetch full member directory for Brought Forward selection
  useEffect(() => {
    if (UserService && typeof UserService.getAll === "function") {
      UserService.getAll()
        .then((res) => {
          const data = (res && res.data) || res || [];
          if (Array.isArray(data)) setAllMembers(data);
        })
        .catch(() => {});
    }
  }, []);

  // Toggle to show/hide timestamps under amounts
  const [showTimestamps, setShowTimestamps] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fast path: call single summary endpoint
      if (typeof UserService.getSummary === "function") {
        try {
          const summaryResp = await UserService.getSummary();
          const summaryData = (summaryResp && summaryResp.data) || summaryResp;
          if (Array.isArray(summaryData) && summaryData.length > 0) {
            setRows(summaryData);
            setLoading(false);
            return;
          }
        } catch (fastErr) {
          console.warn("Single summary endpoint failed, falling back to multi-fetch:", fastErr);
        }
      }

      // Fallback path: use presGetAll only for presidents
      const usersResp = isPresident && typeof UserService.presGetAll === "function"
        ? await UserService.presGetAll()
        : await (UserService.getAll?.());
      const users = (usersResp && usersResp.data) || usersResp || [];

      const results = await Promise.all(
        users.map(async (u) => {
          const id = u._id || u.id || u.registrationNo || u.registration_number;
          const fname = u.firstName || u.firstname || u.first_name || u.name || "";
          const lname = u.lastName || u.lastname || u.last_name || "";
          let username = (lname ? lname + (fname ? " " : "") : "") + fname;
          if (!username.trim()) username = u.username || u.userName || u.email || "Unknown";

          const fetches = Object.entries(servicesByKey).map(async ([key, svc]) => {
            if (!svc || typeof svc.getAll !== "function") return [key, { balance: 0, ts: null }];
            try {
              const res = await svc.getAll(id);
              const items = (res && res.data) || res || [];
              return [key, pickLatestBalanceAndTs(items)];
            } catch {
              return [key, { balance: 0, ts: null }];
            }
          });

          const settled = await Promise.all(fetches);
          const entry = { username };
          entry._id = id;
          entry.id = id;
          entry.registrationNo = u.registrationNo || u.membershipId || "";
          entry.membershipId = u.membershipId || "";
          entry.approvedOrNotApproved = u.approvedOrNotApproved || u.approvalStatus || u.status || "Active";
          entry.sex = String(u.sex || u.gender || "").toLowerCase().trim();
          settled.forEach(([k, obj]) => {
            entry[k] = obj.balance;
            entry[`${k}_ts`] = obj.ts;
          });

          const allTs = Object.keys(entry)
            .filter((k) => k.endsWith("_ts"))
            .map((k) => entry[k])
            .filter(Boolean)
            .map((s) => new Date(s).getTime())
            .filter((n) => Number.isFinite(n));
          entry.lastUpdated = allTs.length ? new Date(Math.max(...allTs)).toISOString() : null;

          return entry;
        })
      );

      setRows(results);
    } catch (err) {
      setError(err?.message || "Failed to load summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [isPresident]); // eslint-disable-line

  const getLast30DaysRange = () => {
    const now = new Date();
    const prior = new Date();
    prior.setDate(prior.getDate() - 30);
    const formatDate = (d) => d.toISOString().slice(0, 10);
    return {
      startDate: formatDate(prior),
      endDate: formatDate(now),
    };
  };

  // search + calendar date range filter (defaults to Last 30 Days)
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [activePreset, setActivePreset] = useState("last30");
  const [startDate, setStartDate] = useState(() => getLast30DaysRange().startDate);
  const [endDate, setEndDate] = useState(() => getLast30DaysRange().endDate);

  const setPresetRange = (preset) => {
    setActivePreset(preset);
    const now = new Date();
    if (preset === "all") {
      setStartDate("");
      setEndDate("");
    } else if (preset === "thisMonth") {
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      setStartDate(`${year}-${month}-01`);
      setEndDate(`${year}-${month}-${String(lastDay).padStart(2, "0")}`);
    } else if (preset === "last30") {
      const prior = new Date();
      prior.setDate(prior.getDate() - 30);
      const formatDate = (d) => d.toISOString().slice(0, 10);
      setStartDate(formatDate(prior));
      setEndDate(formatDate(now));
    } else if (preset === "thisYear") {
      const year = now.getFullYear();
      setStartDate(`${year}-01-01`);
      setEndDate(now.toISOString().slice(0, 10));
    }
    setPage(0);
  };

  // Calculate live counts for each membership status across all rows
  const statusCounts = useMemo(() => {
    let active = 0, nonActive = 0, withdraw = 0, expel = 0, decease = 0;
    rows.forEach((r) => {
      const raw = (r.approvedOrNotApproved || r.status || "Active").toString().trim().toLowerCase();
      if (raw === "active" || raw === "approved" || !r.approvedOrNotApproved) active++;
      else if (raw === "non-active" || raw === "inactive" || raw === "non active") nonActive++;
      else if (raw === "withdraw" || raw === "withdrawn" || raw === "withdrawal") withdraw++;
      else if (raw === "expel" || raw === "expelled") expel++;
      else if (raw === "decease" || raw === "deceased") decease++;
      else active++;
    });
    return { active, nonActive, withdraw, expel, decease, all: rows.length };
  }, [rows]);

  const filtered = useMemo(() => {
    let result = rows;

    if (statusFilter !== "All") {
      result = result.filter((r) => {
        const raw = (r.approvedOrNotApproved || r.status || "Active").toString().trim().toLowerCase();
        if (statusFilter === "Active") return raw === "active" || raw === "approved" || !r.approvedOrNotApproved;
        if (statusFilter === "Non-Active") return raw === "non-active" || raw === "inactive" || raw === "non active";
        if (statusFilter === "Withdraw") return raw === "withdraw" || raw === "withdrawn" || raw === "withdrawal";
        if (statusFilter === "Expel") return raw === "expel" || raw === "expelled";
        if (statusFilter === "Decease") return raw === "decease" || raw === "deceased";
        return false;
      });
    }

    if (query) {
      const q = query.toString().trim().toLowerCase();
      result = result.filter((r) => 
        (r.username || "").toString().toLowerCase().includes(q) ||
        (r.registrationNo || "").toString().toLowerCase().includes(q) ||
        (r.membershipId || "").toString().toLowerCase().includes(q)
      );
    }

    if (startDate || endDate) {
      const startMs = startDate ? new Date(`${startDate}T00:00:00`).getTime() : -Infinity;
      const endMs = endDate ? new Date(`${endDate}T23:59:59.999`).getTime() : Infinity;

      result = result.filter((r) => {
        const candidateTimestamps = [
          r.lastUpdated,
          r.shares_ts,
          r.savings_ts,
          r.loan_ts,
          r.ileya_ts,
          r.dev_ts,
          r.min_ts,
          r.wel_ts,
          r.takaful_ts,
          r.speacc_ts,
          r.business_ts
        ]
          .filter(Boolean)
          .map((s) => new Date(s).getTime())
          .filter((t) => Number.isFinite(t));

        if (candidateTimestamps.length === 0) return false;
        return candidateTimestamps.some((t) => t >= startMs && t <= endMs);
      });
    }

    return result;
  }, [rows, query, statusFilter, startDate, endDate]);

  // member gender counts (dynamically reflects filtered set)
  const memberGenderCounts = useMemo(() => {
    let male = 0, female = 0;
    filtered.forEach((r) => {
      const s = String(r.sex || "").toLowerCase();
      if (s.startsWith("m")) male++;
      else if (s.startsWith("f")) female++;
    });
    return { male, female };
  }, [filtered]);

  // totals computed on filtered set (so search affects totals)
  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, r) => {
        acc.shares += Number(r.shares || 0);
        acc.savings += Number(r.savings || 0);
        acc.loan += Number(r.loan || 0);
        acc.ileya += Number(r.ileya || 0);
        acc.dev += Number(r.dev || 0);
        acc.min += Number(r.min || 0);
        acc.wel += Number(r.wel || 0);
        acc.takaful += Number(r.takaful || 0);
        acc.speacc += Number(r.speacc || 0);
        acc.business += Number(r.business || 0);
        return acc;
      },
      { shares: 0, savings: 0, loan: 0, ileya: 0, dev: 0, min: 0, wel: 0, takaful: 0, speacc: 0, business: 0 }
    );
  }, [filtered]);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const currentPage = Math.min(page, pageCount - 1);
  const startEntry = filtered.length === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endEntry = Math.min((currentPage + 1) * rowsPerPage, filtered.length);

  const pageRows = useMemo(() => {
    const start = currentPage * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, currentPage, rowsPerPage]);

  // export CSV (Excel-friendly) using filtered full data
  const exportCSV = () => {
    const dateRangeLabel = (startDate || endDate)
      ? `Date Range: ${startDate || 'Earliest'} to ${endDate || 'Latest'}`
      : "Date Range: All Time";
    const cols = ["Full Name", "Shares", "Savings", "Loan", "Ileya", "Dev Levy", "Minutes", "Welfare", "Takaful", "Special", ...(canViewBusiness ? ["Business"] : []), "Last Updated"];
    const lines = [
      `"Mutual Trust Cooperative - Financial Summary (${dateRangeLabel})"`,
      cols.join(",")
    ];

    // totals first
    lines.push([
      "Totals",
      totals.shares,
      totals.savings,
      totals.loan,
      totals.ileya,
      totals.dev,
      totals.min,
      totals.wel,
      totals.takaful,
      totals.speacc,
      ...(canViewBusiness ? [totals.business] : []),
      ""
    ].join(","));

    filtered.forEach((r) => {
      lines.push([
        `"${(r.username || "").toString().replace(/"/g, '""')}"`,
        r.shares ?? 0,
        r.savings ?? 0,
        r.loan ?? 0,
        r.ileya ?? 0,
        r.dev ?? 0,
        r.min ?? 0,
        r.wel ?? 0,
        r.takaful ?? 0,
        r.speacc ?? 0,
        ...(canViewBusiness ? [r.business ?? 0] : []),
        r.lastUpdated ? new Date(r.lastUpdated).toLocaleString() : ""
      ].join(","));
    });

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `summary_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // print all pages (open printable window with full table; user can choose Save as PDF)
  const printAll = () => {
    const head = `
      <style>
        table { width: 100%; border-collapse: collapse; font-family: Arial, Helvetica, sans-serif; }
        th { background: #1976d2; color: #fff; padding: 8px; text-align: left; }
        td { border: 1px solid #ddd; padding: 6px; }
        .totals { background: #f5f5f5; font-weight: 700; }
      </style>
    `;
    const cols = ["Full Name","Shares","Savings","Loan","Ileya","Dev Levy","Minutes","Welfare","Takaful","Special", ...(canViewBusiness ? ["Business"] : []), "Last Updated"];
    let html = `<html><head><title>Summary</title>${head}</head><body>`;
    const dateRangeLabel = (startDate || endDate)
      ? `Date Range: ${startDate || 'Earliest'} to ${endDate || 'Latest'}`
      : "Date Range: All Time";
    html += `<div style="margin-bottom: 16px;">
      <h2 style="margin: 0 0 6px 0; color: #0B3B60;">Mutual Trust Cooperative - Financial Portfolio Summary</h2>
      <p style="margin: 0; font-size: 13px; color: #555;">Generated: ${new Date().toLocaleString()} &bull; ${dateRangeLabel} &bull; Total Members: ${filtered.length}</p>
    </div>`;
    html += `<table><thead><tr>${cols.map(c => `<th>${c}</th>`).join("")}</tr></thead><tbody>`;
    // totals
    html += `<tr class="totals"><td>Totals</td><td style="text-align:right">${fNumber(totals.shares)}</td><td style="text-align:right">${fNumber(totals.savings)}</td><td style="text-align:right">${fNumber(totals.loan)}</td><td style="text-align:right">${fNumber(totals.ileya)}</td><td style="text-align:right">${fNumber(totals.dev)}</td><td style="text-align:right">${fNumber(totals.min)}</td><td style="text-align:right">${fNumber(totals.wel)}</td><td style="text-align:right">${fNumber(totals.takaful)}</td><td style="text-align:right">${fNumber(totals.speacc)}</td>${canViewBusiness ? `<td style="text-align:right">${fNumber(totals.business)}</td>` : ""}<td></td></tr>`;
    // rows
    filtered.forEach((r) => {
      html += `<tr>
        <td>${escapeHtml(r.username || "Unknown")}</td>
        <td style="text-align:right">${fNumber(r.shares ?? 0)}</td>
        <td style="text-align:right">${fNumber(r.savings ?? 0)}</td>
        <td style="text-align:right">${fNumber(r.loan ?? 0)}</td>
        <td style="text-align:right">${fNumber(r.ileya ?? 0)}</td>
        <td style="text-align:right">${fNumber(r.dev ?? 0)}</td>
        <td style="text-align:right">${fNumber(r.min ?? 0)}</td>
        <td style="text-align:right">${fNumber(r.wel ?? 0)}</td>
        <td style="text-align:right">${fNumber(r.takaful ?? 0)}</td>
        <td style="text-align:right">${fNumber(r.speacc ?? 0)}</td>
        ${canViewBusiness ? `<td style="text-align:right">${fNumber(r.business ?? 0)}</td>` : ""}
        <td>${r.lastUpdated ? new Date(r.lastUpdated).toLocaleString() : ""}</td>
      </tr>`;
    });
    html += `</tbody></table></body></html>`;

    const w = window.open("", "_blank", "width=1200,height=800");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    // give browser a moment to render then print
    setTimeout(() => { w.print(); }, 500);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 2.5,
          border: "1px solid #E2E8F0",
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)",
        }}
      >
        <Grid container alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={5}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#0F172A", letterSpacing: "-0.01em" }}>
              Financial Portfolio Summary
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", mt: 0.5 }}>
              Comprehensive real-time ledger aggregate across all active member accounts.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.8,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "6px",
                  backgroundColor: "#F1F5F9",
                  border: "1px solid #E2E8F0",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                <span>Male:</span>
                <strong style={{ color: "#0B3B60" }}>{memberGenderCounts.male}</strong>
              </Box>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.8,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "6px",
                  backgroundColor: "#F1F5F9",
                  border: "1px solid #E2E8F0",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                <span>Female:</span>
                <strong style={{ color: "#0B3B60" }}>{memberGenderCounts.female}</strong>
              </Box>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.8,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "6px",
                  backgroundColor: "rgba(5, 150, 105, 0.08)",
                  border: "1px solid rgba(5, 150, 105, 0.2)",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "#047857",
                }}
              >
                <span>Active Ledger Members:</span>
                <strong>{rows.length}</strong>
              </Box>
              {(isAdmin || isPresident || isFinancialI) && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.8,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "6px",
                    backgroundColor: "rgba(13, 148, 136, 0.08)",
                    border: "1px solid rgba(13, 148, 136, 0.25)",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: "#0D9488",
                  }}
                >
                  <span>Total Disbursed to Business:</span>
                  <strong>₦{fNumber(totals.business || 0)}</strong>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} lg={7} sx={{ textAlign: { xs: "left", lg: "right" } }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
                width: "100%",
                justifyContent: { xs: "stretch", sm: "flex-start", lg: "flex-end" }
              }}
            >
              <TextField
                size="small"
                placeholder="Search full name..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value || "");
                  setPage(0);
                }}
                sx={{
                  flex: { xs: "1 1 100%", sm: "1 1 auto" },
                  minWidth: { xs: "100%", sm: 180 },
                  maxWidth: { lg: 220 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#F8FAFC",
                    "& fieldset": { borderColor: "#E2E8F0" },
                    "&:hover fieldset": { borderColor: "#CBD5E1" },
                    "&.Mui-focused fieldset": { borderColor: "#0B3B60" },
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => { setQuery(""); setPage(0); }}
                  sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, borderColor: "#CBD5E1", flex: { xs: 1, sm: "initial" } }}
                >
                  Clear
                </Button>

                {/* Toggle button to show/hide timestamps */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowTimestamps((s) => !s)}
                  sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, borderColor: "#CBD5E1", flex: { xs: 1, sm: "initial" }, whiteSpace: "nowrap" }}
                >
                  {showTimestamps ? "Hide Time" : "Timestamps"}
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={exportCSV}
                  sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, borderColor: "#0B3B60", color: "#0B3B60", flex: { xs: 1, sm: "initial" } }}
                >
                  CSV
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={printAll}
                  sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, flex: { xs: 1, sm: "initial" }, whiteSpace: "nowrap" }}
                >
                  Print PDF
                </Button>
                {canPostBroughtForward && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AccountBalanceWalletIcon />}
                    onClick={() => {
                      setSelectedBfMember(null);
                      setBfModalOpen(true);
                    }}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      backgroundColor: "#0B3B60",
                      "&:hover": { backgroundColor: "#072740" },
                      whiteSpace: "nowrap",
                      width: { xs: "100%", sm: "auto" }
                    }}
                  >
                    Opening Balances (BF)
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Calendar Date Range Filter Bar */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            p: 1.75,
            mb: 2.5,
            borderRadius: "10px",
            backgroundColor: "#F8FAFC",
            border: "1px solid #E2E8F0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B", display: "flex", alignItems: "center", gap: 0.5, fontSize: "0.85rem" }}>
              <span>📅</span> Calendar Range:
            </Typography>
            <TextField
              type="date"
              size="small"
              label="Start Date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setActivePreset("custom");
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 155,
                backgroundColor: "#ffffff",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  "& fieldset": { borderColor: "#CBD5E1" }
                }
              }}
            />
            <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 600 }}>to</Typography>
            <TextField
              type="date"
              size="small"
              label="End Date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setActivePreset("custom");
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 155,
                backgroundColor: "#ffffff",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  "& fieldset": { borderColor: "#CBD5E1" }
                }
              }}
            />
            {(startDate || endDate) && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => setPresetRange("all")}
                sx={{
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  height: 38,
                  borderColor: "#EF4444",
                  color: "#DC2626",
                  "&:hover": { borderColor: "#DC2626", backgroundColor: "rgba(239, 68, 68, 0.04)" }
                }}
              >
                Clear Dates
              </Button>
            )}
            {(startDate || endDate) && (
              <Box
                sx={{
                  px: 1.2,
                  py: 0.4,
                  borderRadius: "6px",
                  backgroundColor: "rgba(11, 59, 96, 0.08)",
                  border: "1px solid rgba(11, 59, 96, 0.2)",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "#0B3B60"
                }}
              >
                Showing {filtered.length} of {rows.length} records
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, mr: 0.5 }}>
              Presets:
            </Typography>
            {[
              { key: "last30", label: "Last 30 Days" },
              { key: "thisMonth", label: "This Month" },
              { key: "thisYear", label: "This Year" },
              { key: "all", label: "All Time" },
            ].map(({ key, label }) => (
              <Button
                key={key}
                size="small"
                variant={activePreset === key ? "contained" : "outlined"}
                onClick={() => setPresetRange(key)}
                sx={{
                  borderRadius: "6px",
                  textTransform: "none",
                  py: 0.4,
                  px: 1.2,
                  fontSize: "0.75rem",
                  minWidth: "auto",
                  fontWeight: 700,
                  backgroundColor: activePreset === key ? "#0B3B60" : "#ffffff",
                  borderColor: activePreset === key ? "#0B3B60" : "#CBD5E1",
                  color: activePreset === key ? "#ffffff" : "#334155",
                  boxShadow: activePreset === key ? "0 2px 6px rgba(11, 59, 96, 0.25)" : "none",
                  "&:hover": {
                    backgroundColor: activePreset === key ? "#082942" : "rgba(11, 59, 96, 0.06)",
                    borderColor: "#082942",
                  },
                }}
              >
                {label}
              </Button>
            ))}
            <Box sx={{ width: "100%", height: 1, borderTop: "1px dashed #E2E8F0", my: 1 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap", width: "100%" }}>
              <Typography variant="caption" sx={{ color: "#334155", fontWeight: 700, mr: 0.5 }}>
                Status Filter:
              </Typography>
              {[
                { key: "Active", label: `Active (${statusCounts.active})`, color: "#059669" },
                { key: "Non-Active", label: `Non-Active (${statusCounts.nonActive})`, color: "#D97706" },
                { key: "Withdraw", label: `Withdraw (${statusCounts.withdraw})`, color: "#854D0E" },
                { key: "Expel", label: `Expel (${statusCounts.expel})`, color: "#DC2626" },
                { key: "Decease", label: `Decease (${statusCounts.decease})`, color: "#475569" },
                { key: "All", label: `All (${statusCounts.all})`, color: "#0B3B60" }
              ].map(({ key, label, color }) => (
                <Button
                  key={key}
                  size="small"
                  variant={statusFilter === key ? "contained" : "outlined"}
                  onClick={() => {
                    setStatusFilter(key);
                    setPage(0);
                  }}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    py: 0.35,
                    px: 1.2,
                    fontSize: "0.75rem",
                    minWidth: "auto",
                    fontWeight: 700,
                    backgroundColor: statusFilter === key ? color : "#ffffff",
                    borderColor: statusFilter === key ? "transparent" : "#CBD5E1",
                    color: statusFilter === key ? "#ffffff" : "#334155",
                    "&:hover": {
                      backgroundColor: statusFilter === key ? color : "#F8FAFC",
                    }
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPresetRange("thisYear")}
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                py: 0.4,
                px: 1.2,
                fontSize: "0.75rem",
                minWidth: "auto",
                fontWeight: 600,
                borderColor: "#CBD5E1",
                color: "#334155"
              }}
            >
              This Year
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 0.5, mb: 1, color: "#64748B" }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                👉 Slide table horizontally to view all columns
              </Typography>
            </Box>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: 2,
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                maxWidth: "100%",
                display: "block",
              }}
            >
              <Table size="small" sx={{ minWidth: 1050, whiteSpace: "nowrap" }}>
                <TableHead sx={{ backgroundColor: "#0B3B60" }}>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Full Name</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Shares</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Savings</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Loan</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Ileya</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Dev. Levy</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Minutes</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Welfare</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Takaful</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Special</TableCell>
                    {canViewBusiness && (
                      <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Business</TableCell>
                    )}
                    <TableCell align="right" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Last Updated</TableCell>
                    {canPostBroughtForward && (
                      <TableCell align="center" sx={{ backgroundColor: "#0B3B60", color: "#ffffff", fontWeight: 700, fontSize: "0.8125rem", py: 1.5 }}>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow sx={{ backgroundColor: "#F8FAFC", borderTop: "2px solid #E2E8F0", borderBottom: "2px solid #E2E8F0" }}>
                    <TableCell sx={{ fontWeight: 800, color: "#0F172A", py: 1.5 }}>TOTALS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>{fNumber(totals.shares)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>{fNumber(totals.savings)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>{fNumber(totals.loan)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>{fNumber(totals.ileya)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>{fNumber(totals.dev)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>{fNumber(totals.min)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>{fNumber(totals.wel)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>{fNumber(totals.takaful)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>{fNumber(totals.speacc)}</TableCell>
                    {canViewBusiness && (
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>{fNumber(totals.business)}</TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 800 }}></TableCell>
                    {canPostBroughtForward && (
                      <TableCell align="center" sx={{ fontWeight: 800 }}></TableCell>
                    )}
                  </TableRow>

                  {pageRows.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#1E293B" }}>
                              {r.username}
                            </Typography>
                            {(r.registrationNo || r.membershipId) && (
                              <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                                {r.registrationNo ? `Reg: ${r.registrationNo}` : `ID: ${r.membershipId}`}
                              </Typography>
                            )}
                          </Box>
                          {r.approvedOrNotApproved && r.approvedOrNotApproved !== "Active" && (
                            <Chip
                              label={r.approvedOrNotApproved}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.6875rem",
                                fontWeight: 700,
                                backgroundColor:
                                  r.approvedOrNotApproved === "Non-Active" ? "#FEF3C7" :
                                  r.approvedOrNotApproved === "Withdraw" ? "#FEF9C3" :
                                  r.approvedOrNotApproved === "Expel" ? "#FEE2E2" :
                                  r.approvedOrNotApproved === "Decease" ? "#F1F5F9" : "#ECFDF5",
                                color:
                                  r.approvedOrNotApproved === "Non-Active" ? "#B45309" :
                                  r.approvedOrNotApproved === "Withdraw" ? "#854D0E" :
                                  r.approvedOrNotApproved === "Expel" ? "#B91C1C" :
                                  r.approvedOrNotApproved === "Decease" ? "#475569" : "#047857",
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box>
                          <Box>{fNumber(r.shares ?? 0)}</Box>
                          {showTimestamps && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.shares_ts ? new Date(r.shares_ts).toLocaleString() : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box>
                          <Box>{fNumber(r.savings ?? 0)}</Box>
                          {showTimestamps && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.savings_ts ? new Date(r.savings_ts).toLocaleString() : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box>
                          <Box>{fNumber(r.loan ?? 0)}</Box>
                          {showTimestamps && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.loan_ts ? new Date(r.loan_ts).toLocaleString() : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box>
                          <Box>{fNumber(r.ileya ?? 0)}</Box>
                          {showTimestamps && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.ileya_ts ? new Date(r.ileya_ts).toLocaleString() : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box>
                          <Box>{fNumber(r.dev ?? 0)}</Box>
                          {showTimestamps && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.dev_ts ? new Date(r.dev_ts).toLocaleString() : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box>
                          <Box>{fNumber(r.min ?? 0)}</Box>
                          {showTimestamps && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.min_ts ? new Date(r.min_ts).toLocaleString() : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box>
                          <Box>{fNumber(r.wel ?? 0)}</Box>
                          {showTimestamps && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.wel_ts ? new Date(r.wel_ts).toLocaleString() : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box>
                          <Box>{fNumber(r.takaful ?? 0)}</Box>
                          {showTimestamps && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.takaful_ts ? new Date(r.takaful_ts).toLocaleString() : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box>
                          <Box>{fNumber(r.speacc ?? 0)}</Box>
                          {showTimestamps && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.speacc_ts ? new Date(r.speacc_ts).toLocaleString() : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {canViewBusiness && (
                        <TableCell align="right">
                          <Box>
                            <Box>{fNumber(r.business ?? 0)}</Box>
                            {showTimestamps && (
                              <Typography variant="caption" color="text.secondary" component="div">
                                {r.business_ts ? new Date(r.business_ts).toLocaleString() : ""}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      )}

                      <TableCell align="right">{r.lastUpdated ? new Date(r.lastUpdated).toLocaleString() : ""}</TableCell>
                      {canPostBroughtForward && (
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const match = allMembers.find((m) => (m._id || m.id) === (r._id || r.id)) || {
                                _id: r._id || r.id,
                                id: r._id || r.id,
                                username: r.username,
                                firstName: r.username,
                                registrationNo: r.registrationNo || r.membershipId,
                              };
                              setSelectedBfMember(match);
                              setBfModalOpen(true);
                            }}
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              textTransform: "none",
                              py: 0.25,
                              px: 1,
                              minWidth: 0,
                              borderRadius: "5px",
                              color: "#0B3B60",
                              borderColor: "rgba(11, 59, 96, 0.3)",
                              "&:hover": {
                                borderColor: "#0B3B60",
                                backgroundColor: "rgba(11, 59, 96, 0.06)",
                              },
                            }}
                          >
                            Set BF
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Fully Responsive Pagination Toolbar */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: "space-between",
                gap: { xs: 1.5, md: 2 },
                py: { xs: 1.5, sm: 1.75 },
                px: { xs: 1.5, sm: 2.5 },
                bgcolor: "#ffffff",
                borderTop: "1px solid #f1f5f9",
              }}
            >
              {/* Section 1: Entries Count & Rows Per Page */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "space-between", md: "flex-start" },
                  gap: { xs: 1.5, sm: 2.5 },
                  flexWrap: "wrap",
                  width: { xs: "100%", md: "auto" },
                }}
              >
                <Typography variant="body2" sx={{ color: "#64748b", fontSize: { xs: "0.78rem", sm: "0.82rem" } }}>
                  Showing <strong style={{ color: "#0f172a" }}>{startEntry}</strong>–
                  <strong style={{ color: "#0f172a" }}>{endEntry}</strong> of{" "}
                  <strong style={{ color: "#0b3b60" }}>{filtered.length}</strong>
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.78rem" }}>
                    Rows:
                  </Typography>
                  <Select
                    size="small"
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(0);
                    }}
                    sx={{
                      height: 28,
                      fontSize: "0.78rem",
                      borderRadius: 1.5,
                      bgcolor: "#f8fafc",
                      "& .MuiSelect-select": { py: 0.3, px: 1 },
                    }}
                  >
                    {[10, 25, 50, 100].map((opt) => (
                      <MenuItem key={opt} value={opt} sx={{ fontSize: "0.78rem" }}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>

              {/* Section 2: Navigation Buttons (Mobile-Adaptive Layout) */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "space-between", md: "flex-end" },
                  gap: { xs: 0.5, sm: 0.75 },
                  width: { xs: "100%", md: "auto" },
                }}
              >
                {/* First Button (Icon-only on mobile, full button on desktop) */}
                <Tooltip title="First Page">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setPage(0)}
                      disabled={currentPage === 0}
                      sx={{
                        display: { xs: "inline-flex", sm: "none" },
                        p: 0.6,
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        "&.Mui-disabled": { borderColor: "#e2e8f0" },
                      }}
                    >
                      <FirstPageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setPage(0)}
                  disabled={currentPage === 0}
                  startIcon={<FirstPageIcon />}
                  sx={{
                    display: { xs: "none", sm: "inline-flex" },
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    py: 0.4,
                    px: 1,
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    "&:disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
                  }}
                >
                  First
                </Button>

                {/* Previous Button */}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  startIcon={<ChevronLeftIcon />}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: { xs: "0.75rem", sm: "0.78rem" },
                    py: 0.4,
                    px: { xs: 1, sm: 1.3 },
                    minWidth: { xs: 68, sm: "auto" },
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    "&:disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>Prev</Box>
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Previous</Box>
                </Button>

                {/* Page Numbers for Desktop (md+) */}
                <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.5, mx: 0.5 }}>
                  {getPageRange(currentPage, pageCount).map((p, idx) => {
                    if (p === "...") {
                      return (
                        <Typography key={`dots-${idx}`} variant="caption" sx={{ px: 0.5, color: "#94a3b8", fontWeight: 700 }}>
                          ...
                        </Typography>
                      );
                    }
                    const isCurrent = p - 1 === currentPage;
                    return (
                      <Button
                        key={p}
                        size="small"
                        variant={isCurrent ? "contained" : "text"}
                        onClick={() => setPage(p - 1)}
                        sx={{
                          minWidth: 30,
                          height: 30,
                          p: 0,
                          borderRadius: "6px",
                          fontWeight: 700,
                          fontSize: "0.78rem",
                          backgroundColor: isCurrent ? "#0b3b60" : "transparent",
                          color: isCurrent ? "#ffffff" : "#334155",
                          "&:hover": {
                            backgroundColor: isCurrent ? "#072740" : "#f1f5f9",
                          },
                        }}
                      >
                        {p}
                      </Button>
                    );
                  })}
                </Box>

                {/* Mobile & Tablet Compact Page Badge */}
                <Chip
                  label={`${currentPage + 1} / ${pageCount}`}
                  size="small"
                  sx={{
                    display: { xs: "inline-flex", md: "none" },
                    height: 28,
                    px: 0.5,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    backgroundColor: "rgba(11, 59, 96, 0.08)",
                    color: "#0b3b60",
                    borderRadius: "6px",
                  }}
                />

                {/* Next Button */}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setPage(Math.min(pageCount - 1, currentPage + 1))}
                  disabled={currentPage >= pageCount - 1}
                  endIcon={<ChevronRightIcon />}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: { xs: "0.75rem", sm: "0.78rem" },
                    py: 0.4,
                    px: { xs: 1, sm: 1.3 },
                    minWidth: { xs: 68, sm: "auto" },
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    "&:disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
                  }}
                >
                  Next
                </Button>

                {/* Last Button (Icon-only on mobile, full button on desktop) */}
                <Tooltip title="Last Page">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setPage(pageCount - 1)}
                      disabled={currentPage >= pageCount - 1}
                      sx={{
                        display: { xs: "inline-flex", sm: "none" },
                        p: 0.6,
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        "&.Mui-disabled": { borderColor: "#e2e8f0" },
                      }}
                    >
                      <LastPageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setPage(pageCount - 1)}
                  disabled={currentPage >= pageCount - 1}
                  endIcon={<LastPageIcon />}
                  sx={{
                    display: { xs: "none", sm: "inline-flex" },
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    py: 0.4,
                    px: 1,
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    "&:disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
                  }}
                >
                  Last
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Brought Forward (Opening Balances) Modal */}
      {canPostBroughtForward && (
        <BroughtForwardModal
          open={bfModalOpen}
          onClose={() => {
            setBfModalOpen(false);
            setSelectedBfMember(null);
          }}
          members={
            allMembers.length > 0
              ? allMembers
              : rows.map((r) => ({
                  _id: r._id || r.id,
                  id: r._id || r.id,
                  username: r.username,
                  firstName: r.username,
                  registrationNo: r.registrationNo || r.membershipId,
                }))
          }
          preselectedMember={selectedBfMember}
          onSuccess={() => {
            loadData();
          }}
        />
      )}
    </Container>
  );
}